import { getName } from "country-list";
import SchoolDefaultLogo from "../../../assets/img/school-default-logo.png";
import {
  beautifyDurationUnit,
  calculateIntakeName,
  toCurrency,
} from "../../../helpers";
import { EducationType } from "../../school/constants/types";
import { PROGRAM_TYPES } from "../constants";
import { IProgramCardBackEndModel, IProgramCardFrontEndModel } from "../types";
import {
  beautifyRequiredDocuments,
  calcFeeWithScholarshipAndFinancialAid,
  sumTuitionAndOtherFeesSupports,
} from "./helperFunctions";

function parser(program: IProgramCardBackEndModel) {
  const isHigher = program.academic.school.typeId === EducationType.Higher;

  const scholarship =
    program.hasTuitionScholarship || program.hasOtherFeesScholarship
      ? "Scholarship is Available"
      : "N/A";

  const financialAid =
    program.hasTuitionFinancialAid || program.hasOtherFeesFinancialAid
      ? "Financial Aid is Available"
      : "N/A";

  const allIntakes = Array.from(
    new Set(
      program.intakes.map((intake) => calculateIntakeName(intake.startDate)),
    ),
  );

  const exams = isHigher ? program.exams : [];

  const previewExams = exams?.map((exam) => exam.name + " - " + exam.score);

  const housingTypeNames = program?.housingTypeNames?.length
    ? program.housingTypeNames
    : ["N/A"];

  const otherFeesTotal = toCurrency(
    program.otherFeesTotal,
    program.currency,
    " ",
    "code",
  );

  const capitalizedRequiredDocuments = beautifyRequiredDocuments(
    program.academic.requiredDocs,
  );

  const { hasSupport: hasTuitionSupport, calcFee: calcTuition } =
    calcFeeWithScholarshipAndFinancialAid(
      program.tuition,
      program.hasTuitionScholarship,
      program.tuitionScholarshipType,
      program.tuitionScholarshipAmount,
      program.hasTuitionFinancialAid,
      program.tuitionFinancialAidType,
      program.tuitionFinancialAidAmount,
      program.currency,
    );

  const { calcFee: calcOtherFees, hasSupport: hasOtherFeesSupport } =
    calcFeeWithScholarshipAndFinancialAid(
      program.otherFeesTotal,
      program.hasOtherFeesScholarship,
      program.otherFeesScholarshipType,
      program.otherFeesScholarshipAmount,
      program.hasOtherFeesFinancialAid,
      program.otherFeesFinancialAidType,
      program.otherFeesFinancialAidAmount,
      program.currency,
    );

  const {
    tuitionOtherFeesSumWithCurrency,
    tuitionOtherFeesWithSupportsSumWithCurrency,
    hasFeesSupport,
  } = sumTuitionAndOtherFeesSupports(
    program.tuition,
    hasTuitionSupport,
    calcTuition,
    program.otherFeesTotal,
    hasOtherFeesSupport,
    calcOtherFees,
    program.currency,
  );

  const programDuration = `${program.durationAmount} ${beautifyDurationUnit(
    program.durationAmount,
    program.durationUnit,
    "Program",
  )}`;

  const tuitionDuration = `${
    program.tuitionDurationAmount == null
      ? ""
      : program.tuitionDurationAmount + " "
  }
        ${beautifyDurationUnit(
          program.tuitionDurationAmount,
          program.tuitionDurationUnit,
          "Program",
        )}`;

  const totalTuitionWithUnit =
    tuitionOtherFeesWithSupportsSumWithCurrency + " / " + tuitionDuration;

  const schoolImage = program.academic.school.image
    ? program.academic.school.image.preview
    : SchoolDefaultLogo;

  const parsedProgram: IProgramCardFrontEndModel = {
    exams,
    isHigher,
    allIntakes,
    schoolImage,
    scholarship,
    financialAid,
    previewExams,
    otherFeesTotal,
    hasFeesSupport,
    housingTypeNames,
    programDuration,
    capitalizedRequiredDocuments,
    tuitionOtherFeesSumWithCurrency,
    totalTuitionWithUnit,
    id: program.id,
    isMasked: program.isMasked,
    branches: program.branches,
    majorName: program.majorName,
    grade: program.academic.grade,
    levelName: program.academic.levelName,
    typeId: program.academic.school.typeId,
    schoolName: program.academic.school.name,
    degreeName: program.academic.degree?.name,
    schoolTypeName: program.academic.schoolTypeName,
    programType: PROGRAM_TYPES.find((type) => type.id === program.programType)!
      .name,
    schoolCountry:
      program.academic.school.country &&
      getName(program.academic.school.country)!,
  };

  return parsedProgram;
}

export function parseToCardsFrontEndModel(response) {
  return {
    data: response.data?.data.map((program) => parser(program)),
    totalCount: response.data?.totalCount,
  };
}
